import { useCallback, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setPreferencesPartial } from "../../../store/expertise/expertise.reducer";
import FiltersMultiAutocomplete from "./FiltersMultiAutocomplete";
import {
	buildProfessionOptions,
	getSelectedProfessionOptions,
	hasAuthorizedProfessionOrSpecialite,
	normalizeProfessionSelection,
	type InputFilter,
} from "../../../../core/utils/professions.specialites.utils";

export default function ProfessionsSpecialitesAutorisees() {
	const dispatch = useAppDispatch();
	const { filters, preferences } = useAppSelector((state) => ({
		filters: state.expertise.professionsAndSpecialites,
		preferences: state.expertise.preferences,
	}));

	// Transforme les professions PS/UO du store en options hiérarchisées pour l'autocomplete.
	const options = useMemo(() => buildProfessionOptions(filters), [filters]);
	// Reconstruit les coches affichées à partir des préférences sauvegardées.
	const selectedOptions = useMemo(() => getSelectedProfessionOptions(options, preferences?.preferencesRestriction), [options, preferences?.preferencesRestriction]);
	const restriction = preferences?.preferencesRestriction;
	const isSelectionInvalid = restriction?.limitToAutorizedProfessionsAndSpecialites === true && !hasAuthorizedProfessionOrSpecialite(restriction);

	const updateSelection = useCallback(
		(selection: InputFilter[]) => {
			// Applique les règles profession/spécialités avant de sauvegarder la sélection dans le store.
			dispatch(
				setPreferencesPartial({
					preferencesRestriction: normalizeProfessionSelection(options, selectedOptions, selection),
				})
			);
		},
		[dispatch, options, selectedOptions]
	);

	const resetSelection = useCallback(() => {
		// Une sélection vide active la restriction et interdit toutes les professions et spécialités.
		dispatch(
			setPreferencesPartial({
				preferencesRestriction: {
					limitToAutorizedProfessionsAndSpecialites: true,
					autorizedProfessionIds: [],
					autorizedSpecialites: [],
				},
			})
		);
	}, [dispatch]);

	useEffect(() => {
		const restriction = preferences?.preferencesRestriction;
		// Toutes les professions sélectionnées équivalent à l'absence de restriction.
		if (
			options.length > 0 &&
			restriction &&
			((restriction.autorizedProfessionIds === undefined && restriction.autorizedSpecialites === undefined) ||
				(restriction.limitToAutorizedProfessionsAndSpecialites && selectedOptions.length === options.length))
		) {
			updateSelection(options);
		}
	}, [options, preferences?.preferencesRestriction, selectedOptions, updateSelection]);

	return (
		<Grid item marginLeft={4} marginTop={0.5}>
			<FiltersMultiAutocomplete
				label="Profession / spécialité"
				options={options}
				value={selectedOptions}
				error={isSelectionInvalid}
				onChange={updateSelection}
				reset={resetSelection}
			/>
		</Grid>
	);
}
